import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import {
  ArchiveFolderRequest,
  CreateFolderRequest,
  GetFoldersRequest,
  UpdateFolderRequest,
} from "@/types/requests";
import { Folder, FolderType } from "@/types/resources";
import {
  CreateFolderResponse,
  GetFoldersResponse,
  UpdateFolderResponse,
} from "@/types/responses";

const useFolders = (type: FolderType, archived?: boolean) => {
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient("/folders");

  const getFolders = useCallback(
    (params: GetFoldersRequest) => {
      client
        .get<GetFoldersResponse>("", { params })
        .then((response) => {
          setFolders(response.data.folders);
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client]
  );

  // Re-enter the loading state during render (not in an effect) when the
  // query inputs change, so the UI shows loading while refetching.
  const inputsKey = JSON.stringify([type, archived]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setLoading(true);
    setError(null);
  }

  useEffect(() => {
    getFolders({ archived: archived || undefined, type });
  }, [type, archived, getFolders]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    getFolders({ archived: archived || undefined, type });
  }, [getFolders, type, archived]);

  return {
    error,
    folders,
    loading,
    refresh,
  };
};

const useCreateFolder = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/folders");

  const createFolder = useCallback(
    async (data: CreateFolderRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          CreateFolderResponse,
          AxiosResponse<CreateFolderResponse>,
          CreateFolderRequest
        >("", {
          name: data.name.trim(),
          parent_id: data.parent_id,
          type: data.type,
        });
        return response.data.folder;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    createFolder,
    error,
    loading,
  };
};

const useUpdateFolder = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/folders");

  const updateFolder = useCallback(
    async (folderId: string, data: UpdateFolderRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.put<
          UpdateFolderResponse,
          AxiosResponse<UpdateFolderResponse>,
          UpdateFolderRequest
        >(`/${folderId}`, {
          name: data.name.trim(),
          parent_id: data.parent_id,
        });
        return response.data.folder;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    loading,
    updateFolder,
  };
};

const useArchiveFolder = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/folders");

  const archiveFolder = useCallback(
    async (folderId: string, archived: boolean) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.patch<
          UpdateFolderResponse,
          AxiosResponse<UpdateFolderResponse>,
          ArchiveFolderRequest
        >(`/${folderId}`, { archived });
        return response.data.folder;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    archiveFolder,
    error,
    loading,
  };
};

const useDeleteFolder = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/folders");

  const deleteFolder = useCallback(
    async (folderId: string): Promise<boolean> => {
      setError(null);
      setLoading(true);

      try {
        await client.delete(`/${folderId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    deleteFolder,
    error,
    loading,
  };
};

export {
  useArchiveFolder,
  useCreateFolder,
  useDeleteFolder,
  useFolders,
  useUpdateFolder,
};
